import { useState, useRef, useCallback, useEffect } from "react";

type TimerState = "idle" | "running" | "paused";

const STORAGE_KEY = (matchId: number) => `match_timer_${matchId}`;

interface TimerStorage {
  seconds: number;
  state: TimerState;
  startedAt: number; // epoch ms when timer last started
  secondsAtStart: number; // seconds value when last started
}

export function useMatchTimer(matchId: number) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Restore from localStorage on mount
  const getInitialState = (): { seconds: number; state: TimerState } => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY(matchId));
      if (!stored) return { seconds: 0, state: "idle" };

      const data: TimerStorage = JSON.parse(stored);

      if (data.state === "running" && data.startedAt > 0) {
        // Calculate how many seconds passed since page left
        const elapsed = Math.floor((Date.now() - data.startedAt) / 1000);
        return {
          seconds: data.secondsAtStart + elapsed,
          state: "running",
        };
      }

      // Paused — restore exact second it was paused at
      return { seconds: data.seconds, state: data.state };
    } catch {
      return { seconds: 0, state: "idle" };
    }
  };

  const initial = getInitialState();
  const [seconds, setSeconds] = useState(initial.seconds);
  const [state, setState] = useState<TimerState>(initial.state);
  const secondsRef = useRef(initial.seconds); // track latest seconds for storage

  // Keep ref in sync
  useEffect(() => {
    secondsRef.current = seconds;
  }, [seconds]);

  // Save to localStorage whenever state changes
  const persist = useCallback(
    (newSeconds: number, newState: TimerState, isStarting = false) => {
      const data: TimerStorage = {
        seconds: newSeconds,
        state: newState,
        startedAt: isStarting ? Date.now() : 0,
        secondsAtStart: newSeconds,
      };
      localStorage.setItem(STORAGE_KEY(matchId), JSON.stringify(data));
    },
    [matchId],
  );
  // Auto-resume if it was running before refresh
  useEffect(() => {
    if (initial.state === "running") {
      intervalRef.current = setInterval(() => {
        setSeconds((prev) => {
          const next = prev + 1;
          secondsRef.current = next;
          return next;
        });
      }, 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (totalSeconds: number): string => {
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const start = useCallback(
    (fromSeconds = 0) => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setSeconds(fromSeconds);
      secondsRef.current = fromSeconds;
      setState("running");
      persist(fromSeconds, "running", true); // ← isStarting = true

      intervalRef.current = setInterval(() => {
        setSeconds((prev) => {
          const next = prev + 1;
          secondsRef.current = next;
          return next;
        });
      }, 1000);
    },
    [persist],
  );

  // Pause — resumes from exact same second
  const pause = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setState("paused");
    persist(secondsRef.current, "paused");
  }, [persist]);

  // Resume from wherever it was paused
  const resume = useCallback(() => {
    setState("running");
    persist(secondsRef.current, "running", true); // ← isStarting = true

    intervalRef.current = setInterval(() => {
      setSeconds((prev) => {
        const next = prev + 1;
        secondsRef.current = next;
        return next;
      });
    }, 1000);
  }, [persist]);

  // Half time — pauses at current second, next action starts from 45:00
  const halfTime = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setState("paused");
    persist(secondsRef.current, "paused");
  }, [persist]);

  // Start second half always from 45:00
  const startSecondHalf = useCallback(() => {
    start(45 * 60);
  }, [start]);

  const stop = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setState("idle");
    setSeconds(0);
    localStorage.removeItem(STORAGE_KEY(matchId));
  }, [matchId]);

  const isFirstHalf = seconds < 45 * 60;
  const isHalfTime =
    state === "paused" && seconds >= 44 * 60 && seconds < 46 * 60;
  const isFullTime = seconds >= 90 * 60;
  const isExtraTime = seconds > 90 * 60;

  return {
    seconds,
    state,
    formatted: formatTime(seconds),
    isFirstHalf,
    isHalfTime,
    isFullTime,
    isExtraTime,
    start,
    pause,
    resume,
    halfTime,
    startSecondHalf,
    stop,
  };
}
